// Styles
import 'react-toastify/dist/ReactToastify.css';
import './index.css';

import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { lazy, Suspense, useEffect } from 'react';
import { ToastContainer } from 'react-toastify';

import { queryClient } from './config/query-client';
import { queryPersister, shouldPersistQuery } from './config/query-persister';
import { NotificationProvider } from './context/notification-context';
import { TenantProvider } from './context/tenant-context';
import { TimezoneProvider } from './context/timezone-context';
import MainAppRoutes from './routes';
import { setupChunkErrorHandler } from './utils/chunk-error-handler';
import { cleanLoginRequiredCallbackFromUrl } from './utils/keycloak-url-cleanup';

const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then(m => ({
        default: m.ReactQueryDevtools,
      }))
    )
  : null;

function App() {
  useEffect(() => {
    // Set up global chunk error handler to handle failed dynamic imports
    setupChunkErrorHandler();
    cleanLoginRequiredCallbackFromUrl();
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        // Tab lifetime is the cap (sessionStorage clears on close), but set
        // a 24h ceiling for safety.
        maxAge: 24 * 60 * 60 * 1000,
        dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
      }}
    >
      <TenantProvider>
        <TimezoneProvider>
          <NotificationProvider maxNotifications={5}>
            <MainAppRoutes />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            {ReactQueryDevtools && (
              <Suspense fallback={null}>
                <ReactQueryDevtools
                  initialIsOpen={false}
                  position="bottom-right"
                />
              </Suspense>
            )}
          </NotificationProvider>
        </TimezoneProvider>
      </TenantProvider>
    </PersistQueryClientProvider>
  );
}

export default App;
